/* ==========================================================================
   LibertyPaw — mobile drawer behaviour (Redesign Step 5, 2026-07-31)

   Deliberately vanilla, with no dependency on the theme's Alpine `modals`
   store. That store's `register('nav','leftDrawer')` call happens only in
   assets/custom.js, gated on a #left-drawer-slot element that is teleported in
   by a <data-island on="load">, so there is a genuine race in which
   $store.modals.open('nav') silently no-ops. This drawer owns its own state.

   Closes on: X button, backdrop click, Escape.
   Locks background scroll, traps focus, restores focus on close.

   2026-09-25 (audit item A26): the page behind the open drawer is made inert so it
   cannot be tabbed into; the closed drawer is inert; focus that escapes is pulled
   back; select/textarea count as focusable; double-init guard. The drawer markup
   (snippets/lp-mobile-drawer.liquid) now starts inert, so the two ship together.
   ========================================================================== */
(function () {
  'use strict';

  function init() {
    var drawer = document.getElementById('lp-drawer');
    var backdrop = document.getElementById('lp-drawer-backdrop');
    if (!drawer || !backdrop || drawer.dataset.lpBound) return;
    drawer.dataset.lpBound = "true";

    // The header section carries Tailwind `relative z-40`, which creates a
    // stacking context its descendants can never escape. Reparent to <body>
    // so the fixed drawer really does sit above the page.
    if (drawer.parentNode !== document.body) document.body.appendChild(drawer);
    if (backdrop.parentNode !== document.body) document.body.appendChild(backdrop);

    var lastFocused = null;
    var backgroundState = [];

    var FOCUSABLE = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'summary',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    function isOpen() {
      return drawer.classList.contains('is-open');
    }

    function focusable() {
      return Array.prototype.filter.call(
        drawer.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.tabIndex >= 0 && !el.closest("[inert]") && (el.offsetWidth > 0 || el.offsetHeight > 0);
        }
      );
    }

    function open() {
      if (isOpen()) return;
      lastFocused = document.activeElement;
      drawer.inert = false;
      drawer.classList.add('is-open');
      backdrop.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      backgroundState = [];
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === drawer || el === backdrop || /^(SCRIPT|STYLE|LINK)$/.test(el.tagName)) return;
        backgroundState.push({ element: el, inert: el.inert });
        el.inert = true;
      });
      // The scroll lock must reach <html>: the documentElement is the scrolling
      // box on this page, so a body-only overflow:hidden does not stop the page
      // scrolling behind the drawer (confirmed live in Chrome, 2026-08-01).
      document.documentElement.classList.add('lp-drawer-open');
      document.body.classList.add('lp-drawer-open');

      var toggles = document.querySelectorAll('.custom-header-menu-icon');
      for (var i = 0; i < toggles.length; i++) {
        toggles[i].setAttribute('aria-expanded', 'true');
      }

      // Focus after the open frame has painted — focusing synchronously, while
      // the drawer's visibility transition has not started, can be dropped.
      var closeBtn = drawer.querySelector('.lp-drawer__close');
      if (closeBtn) {
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () {
            if (isOpen()) closeBtn.focus();
          });
        });
      }
    }

    function close() {
      if (!isOpen()) return;
      drawer.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      backgroundState.forEach(function (state) { state.element.inert = state.inert; });
      backgroundState = [];
      document.documentElement.classList.remove('lp-drawer-open');
      document.body.classList.remove('lp-drawer-open');

      var toggles = document.querySelectorAll('.custom-header-menu-icon');
      for (var i = 0; i < toggles.length; i++) {
        toggles[i].setAttribute('aria-expanded', 'false');
      }

      var returnTarget = lastFocused;
      if (!returnTarget || !returnTarget.isConnected || !(returnTarget.offsetWidth || returnTarget.offsetHeight)) {
        returnTarget = document.getElementById('header-search');
      }
      if (returnTarget && returnTarget.isConnected && (returnTarget.offsetWidth || returnTarget.offsetHeight)) returnTarget.focus();
      lastFocused = null;
      drawer.inert = true;
      drawer.setAttribute('aria-hidden', 'true');
    }

    // The theme's hamburger also fires $store.modals.open('nav') via an inline
    // onclick and an Alpine @click. Capture-phase + stopPropagation keeps the
    // theme's own left drawer from opening underneath this one.
    document.addEventListener(
      'click',
      function (event) {
        var toggle = event.target.closest && event.target.closest('.custom-header-menu-icon');
        if (!toggle) return;
        if (window.matchMedia('(max-width: 900px)').matches === false) return;
        event.preventDefault();
        event.stopPropagation();
        open();
      },
      true
    );

    drawer.addEventListener('click', function (event) {
      var closer = event.target.closest && event.target.closest('[data-lp-drawer-close]');
      if (closer) {
        event.preventDefault();
        close();
      }
    });

    backdrop.addEventListener('click', close);

    document.addEventListener('keydown', function (event) {
      if (!isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      var items = focusable();
      if (!items.length) { event.preventDefault(); drawer.focus(); return; }

      var first = items[0];
      var last = items[items.length - 1];

      if (!drawer.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('focusin', function (event) {
      if (!isOpen() || drawer.contains(event.target)) return;
      var items = focusable();
      (items[0] || drawer).focus();
    });

    // Leaving mobile with the drawer open would strand a scroll lock.
    var mq = window.matchMedia('(max-width: 900px)');
    var onChange = function (event) {
      if (!event.matches) close();
    };
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(onChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
